import { useEffect, useState } from "react";

const HTML_ESCAPES: Record<string, string> = { "<": "&lt;", ">": "&gt;", "&": "&amp;" };

/**
 * Lazy-load `marked` and render a markdown string as sanitized-ish HTML.
 *
 * Designed for surfaces where the source is short prose (LLM reasoning,
 * framework body markdown, etc.) and where lazy-loading the dependency
 * keeps initial bundle weight off the critical path.
 *
 * Falls back to escaped <pre> if `marked` rejects the input — paranoid
 * but cheap. The output is intended for `dangerouslySetInnerHTML`; the
 * caller is responsible for trust assumptions about the source.
 */
export function useMarkdownHtml(source: string): string {
  const [html, setHtml] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { marked } = await import("marked");
      if (cancelled) return;
      try {
        const out = marked.parse(source, { async: false });
        setHtml(typeof out === "string" ? out : String(out));
      } catch {
        setHtml(`<pre>${source.replace(/[<>&]/g, (c) => HTML_ESCAPES[c])}</pre>`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source]);
  return html;
}
